import { create } from '@mui/material/styles/createTransitions';
import { api, driverServesCarType } from 'common';
import {
  validateBookingObj,
  getEnabledCityOptions,
  resolveCanonicalCity,
} from 'common/sharedFunctions';
import { editUser } from 'common/src/actions/usersactions';
import { Roles } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

const useAddBooking = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    getEstimate,
    GetDistance,
    addBooking,
    addUserAndReturnUid,
    fetchUsersOnce,
  } = api;

  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const usersData = useSelector(state => state.usersdata);
  const estimateData = useSelector(state => state.estimatedata);
  const cartypes = useSelector(state => state.cartypes.cars);
  const countriesdata = useSelector(state => state.countriesdata);

  const [estimateModalStatus, setEstimateModalStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [errors, setErrors] = useState([]);

  const [tempRoute, setTempRoute] = useState();
  const [name, setName] = useState('');
  // Ciudad del pasajero. Por defecto la del operador; si no, Cúcuta (histórico).
  const [city, setCity] = useState(
    auth && auth.profile && auth.profile.city ? auth.profile.city : 'Cúcuta',
  );

  // Canoniza la ciudad elegida contra las ciudades habilitadas (o su forma
  // canónica si no casa con ninguna).
  const canonicalPassengerCity = () =>
    resolveCanonicalCity(city, getEnabledCityOptions(countriesdata)) || 'CUCUTA';
  const [rideData, setRideData] = useState({
    pickupAddress: null,
    dropAddress: null,
    customer: {
      firstName: null,
      mobile: '',
    },
    driver: null,
    status: null,
    date: null,
    price: null,
    carDetails: cartypes[0],
    instructionData: {
      deliveryPerson: '',
      deliveryPersonPhone: '',
      pickUpInstructions: '',
      deliveryInstructions: '',
      parcelTypeIndex: 0,
      optionIndex: 0,
      parcelTypeSelected: null,
      optionSelected: null,
    },
  });

  const loadCurrentUser = () => {
    if (auth.profile.usertype === Roles.CUSTOMER) {
      setRideData({
        ...rideData,
        customer: {
          firstName: auth.profile.firstName,
          lastName: auth.profile.lastName ? auth.profile.lastName : '',
          uid: auth.profile.uid,
          mobile: auth.profile.mobile ? auth.profile.mobile : '',
          email: auth.profile.email ? auth.profile.email : '',
          pushToken: auth.profile.pushToken ? auth.profile.pushToken : null,
        },
      });
    }
  };

  const loadCustomers = () => {
    setLoading(true);
    if (usersData.users) {
      const customers = usersData.users
        .filter(
          user =>
            user.usertype !== Roles.DRIVER &&
            user.mobile !== '' &&
            user.firstName !== '',
        )
        .map(customer => {
          return {
            firstName: customer.firstName,
            lastName: customer.lastName,
            mobile: customer.mobile,
            email: customer.email,
            uid: customer.id,
            city: customer.city || '',
            description: `${customer.firstName} ${customer.lastName} - ${String(
              customer.mobile,
            ).replace('+57', '')}`,
            pushToken: customer.pushToken,
            rideHistory: customer.rideHistory,
          };
        });
      setCustomers(customers);
    }
    setLoading(false);
  };

  const loadDrivers = () => {
    setLoading(true);
    if (usersData.users) {
      const drivers = usersData.users
        .filter(user => user.usertype === Roles.DRIVER)
        .map(driver => {
          return {
            uid: driver.id,
            location: driver.location,
            carType: driver.carType ? driver.carType : 'taxi',
            // Clasificación de tier(s) hecha por el admin en /drivers. Vacío/
            // ausente ⇒ el vehículo atiende todas las categorías.
            carTypes: Array.isArray(driver.carTypes) ? driver.carTypes : null,
            fleetadmin: driver.fleetadmin ? driver.fleetadmin : null,
          };
        });
      setDrivers(drivers);
    }
    setLoading(false);
  };

  const updateSelectedCustomer = newCustomer => {
    if (!newCustomer) {
      setName('');
      setCity(
        auth && auth.profile && auth.profile.city
          ? auth.profile.city
          : 'Cúcuta',
      );
      setRideData({
        ...rideData,
        customer: {
          firstName: '',
          mobile: '',
        },
        pickupAddress: null,
        dropAddress: null,
      });
      return;
    }

    console.log('newCustomer', newCustomer);

    // Crear los objetos de dirección con la estructura correcta
    const pickupAddress = newCustomer.rideHistory?.lastOrigin?.address
      ? {
          coords: {
            lat: newCustomer.rideHistory.lastOrigin.lat,
            lng: newCustomer.rideHistory.lastOrigin.lng,
          },
          addressName: newCustomer.rideHistory.lastOrigin.address,
          add: newCustomer.rideHistory.lastOrigin.address,
          description: newCustomer.rideHistory.lastOrigin.address,
        }
      : null;

    const dropAddress = newCustomer.rideHistory?.lastDestination?.address
      ? {
          coords: {
            lat: newCustomer.rideHistory.lastDestination.lat,
            lng: newCustomer.rideHistory.lastDestination.lng,
          },
          addressName: newCustomer.rideHistory.lastDestination.address,
          add: newCustomer.rideHistory.lastDestination.address,
          description: newCustomer.rideHistory.lastDestination.address,
        }
      : null;

    const newRide = {
      ...rideData,
      customer: newCustomer,
      pickupAddress: pickupAddress,
      dropAddress: dropAddress,
    };

    console.log('newRide', newRide);
    setRideData(newRide);
    setName(newCustomer.firstName);
    if (newCustomer.city) {
      setCity(newCustomer.city);
    }
  };

  const updateCustomerName = name => {
    setName(name);
  };

  const updatePickupAddress = address => {
    setRideData({ ...rideData, pickupAddress: address });
  };

  const updateDropAddress = address => {
    setRideData({ ...rideData, dropAddress: address });
  };

  const updateCustomerPhone = phone => {
    if (!phone) {
      setRideData({
        ...rideData,
        customer: {
          firstName: '',
          mobile: '',
        },
      });
      return;
    }
    setRideData({
      ...rideData,
      customer: {
        firstName: '',
        mobile: phone,
      },
    });
  };

  const updateCarDetails = carDetails => {
    setRideData({ ...rideData, carDetails });
  };

  const updateInstructions = instructionData => {
    setRideData({ ...rideData, instructionData });
  };

  const estimateRouteDetails = e => {
    e.preventDefault();
    if (validateBookingData() === false) {
      alert(
        `[ERROR] Por favor verifique los siguientes datos obligatorios \n ${errors.join(
          '\n',
        )}`,
      );
      return;
    }

    const { pickupAddress, dropAddress } = rideData;

    const directionService = new window.google.maps.DirectionsService();

    directionService.route(
      {
        origin: new window.google.maps.LatLng(
          pickupAddress.coords.lat,
          pickupAddress.coords.lng,
        ),
        destination: new window.google.maps.LatLng(
          dropAddress.coords.lat,
          dropAddress.coords.lng,
        ),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const route = {
            distance_in_km: result.routes[0].legs[0].distance.value / 1000,
            time_in_secs: result.routes[0].legs[0].duration.value,
            polylinePoints: result.routes[0].overview_polyline,
          };
          setTempRoute(route);

          let estimateRequest = {
            pickup: pickupAddress,
            drop: dropAddress,
            carDetails: rideData.carDetails,
            instructionData: rideData.instructionData,
            routeDetails: route,
          };

          dispatch(getEstimate(estimateRequest));
          setEstimateModalStatus(true);
        }
      },
    );
    //}
  };

  const estimateTripData = e => {
    e.preventDefault();
    //setOptionModalStatus(false);
    let estimateRequest = {
      pickup: rideData.pickupAddress,
      drop: rideData.dropAddress,
      carDetails: rideData.carDetails,
      instructionData: rideData.instructionData,
      routeDetails: tempRoute,
    };
    dispatch(getEstimate(estimateRequest));
  };

  const confirmBooking = async (e, additionalComments = '') => {
    e.preventDefault();

    const user = await validUser();
    if (!user) {
      return;
    }

    const selectedCarTypeName = rideData.carDetails
      ? rideData.carDetails.name
      : null;

    let availableDrivers = drivers.filter(driver => driver.location !== null);
    availableDrivers = availableDrivers.filter(driver => {
      const distance = GetDistance(
        rideData.pickupAddress.coords.lat,
        rideData.pickupAddress.coords.lng,
        driver.location.lat,
        driver.location.lng,
      );
      driver['distance'] = distance;
      return settings.driverRadius >= distance;
    });

    // Sólo se solicita a conductores que atienden la categoría elegida.
    availableDrivers = availableDrivers.filter(driver =>
      driverServesCarType(driver, selectedCarTypeName),
    );

    if (availableDrivers.length === 0) {
      alert(
        t('no_driver_found_for_type', {
          defaultValue: t('no_driver_found_alert_messege'),
        }),
      );
      return;
    }

    let driverEstimates = {};

    for (let driver in drivers) {
      driverEstimates[driver['uid']] = {
        distance: driver['distance'],
        timein_text: (driver['distance'] * 2 + 1).toFixed(0) + ' min',
      };
    }

    const adaptedDriversUidData = {};

    availableDrivers.forEach(driver => {
      adaptedDriversUidData[driver.uid] = true;
    });

    let bookingObject = {
      pickup: rideData.pickupAddress,
      drop: rideData.dropAddress,
      carDetails: rideData.carDetails,
      userDetails: user,
      estimate: estimateData.estimate,
      instructionData: rideData.instructionData,
      tripInstructions: '',
      roundTrip: false,
      tripdate: new Date().getTime(),
      bookLater: false,
      settings: settings,
      booking_type_admin: [Roles.ADMIN, Roles.MANAGER].includes(
        auth.profile.usertype,
      )
        ? true
        : false,
      fleetadmin:
        auth.profile.usertype === Roles.FLEET_ADMIN ? auth.profile.uid : null,
      cabCompany:
        auth.profile.usertype === Roles.CAB_COMPANY ? auth.profile.uid : null,
      requestedDrivers: adaptedDriversUidData,
      driverEstimates: driverEstimates,
      payment_mode: 'cash',
      booking_from_web: true,
    };

    setLoading(true);

    const result = validateBookingObj(
      t,
      bookingObject,
      rideData.instructionData,
    );
    if (result.error) {
      setLoading(false);
      alert('[ERROR] Por favor verifique los datos de la reserva');
    } else {
      let bookingData = result.bookingObject;
      bookingData.userDetails.firstName = user.firstName;
      bookingData.userDetails.lastName = user.lastName;
      bookingData.userDetails.email = user.email ? user.email : '';
      bookingData.comments = additionalComments;

      dispatch(addBooking(bookingData));
      setEstimateModalStatus(true);
      setLoading(false);
      // navigate('/bookings', { replace: true });
    }
  };

  const validateBookingData = () => {
    const { pickupAddress, dropAddress, customer } = rideData;
    const errors = [];

    if (!pickupAddress) {
      errors.push('pickupAddress');
    }
    if (!dropAddress) {
      errors.push('dropAddress');
    }
    if (!customer.mobile) {
      errors.push('mobile');
    }

    // Mejorar validación del nombre
    const fullName = name.trim() || customer.firstName || '';
    if (!fullName) {
      errors.push('firstName');
    }

    setErrors(errors);
    return errors.length === 0;
  };

  const validUser = async () => {
    if (!rideData.customer.uid) {
      const user = await createNewBasicUser();
      return user;
    }
    return rideData.customer;
  };

  const createNewBasicUser = async () => {
    const pickup = rideData.pickupAddress || {};
    const drop = rideData.dropAddress || {};

    // Obtener el nombre desde 'name' o desde customer.firstName, con fallback
    const fullName = name.trim() || rideData.customer.firstName || '';
    const nameParts = fullName.split(' ').filter(part => part.length > 0);

    // Función helper para sanitizar valores undefined
    const sanitizeValue = (value, defaultValue = '') => {
      return value !== undefined ? value : defaultValue;
    };

    const user = {
      firstName: nameParts[0] || 'Sin nombre', // Fallback si no hay nombre
      lastName: sanitizeValue(nameParts.slice(1).join(' '), ''), // Unir resto de nombres como apellido
      mobile: sanitizeValue(rideData.customer.mobile, ''),
      email: '', // Siempre inicializar email como string vacío
      city: canonicalPassengerCity(),
      pushToken: null, // Usar null en lugar de undefined
      rideHistory: {
        lastOrigin: {
          address: sanitizeValue(pickup?.addressName, ''),
          lat: pickup?.coords?.lat || null,
          lng: pickup?.coords?.lng || null,
        },
        lastDestination: {
          address: sanitizeValue(drop?.addressName, ''),
          lat: drop?.coords?.lat || null,
          lng: drop?.coords?.lng || null,
        },
      },
      createdAt: new Date().getTime(),
      usertype: Roles.CUSTOMER,
      webFlag: true,
    };

    // Limpiar cualquier valor undefined del objeto
    const cleanUser = Object.fromEntries(
      Object.entries(user).map(([key, value]) => [
        key,
        value === undefined ? null : value,
      ]),
    );

    try {
      const userUid = await dispatch(addUserAndReturnUid(cleanUser));
      cleanUser['uid'] = userUid;
      console.log('New User Created:', cleanUser);

      setRideData({
        ...rideData,
        customer: { ...cleanUser },
      });

      await dispatch(fetchUsersOnce());
      return cleanUser;
    } catch (err) {
      alert(
        (err.msg || err.message) +
          '\n[ERROR] Hubo un error intentando realizar la reserva, verifique los datos e intente nuevamente',
      );
      throw err; // Re-throw para que el llamador pueda manejarlo
    }
  };

  // 2. Corrección en updateBasicUser
  const updateBasicUser = async existingUser => {
    const pickup = rideData.pickupAddress || {};
    const drop = rideData.dropAddress || {};

    // Obtener el nombre actualizado
    const fullName =
      name.trim() ||
      rideData.customer.firstName ||
      existingUser.firstName ||
      '';
    const nameParts = fullName.split(' ').filter(part => part.length > 0);

    // Función helper para sanitizar valores undefined
    const sanitizeValue = (value, defaultValue = '') => {
      return value !== undefined ? value : defaultValue;
    };

    // Construir los datos actualizados del usuario
    const updatedUser = {
      ...existingUser, // Mantener todos los datos existentes
      firstName: nameParts[0] || existingUser.firstName || 'Sin nombre',
      lastName: sanitizeValue(
        nameParts.slice(1).join(' ') || existingUser.lastName,
        '',
      ),
      mobile: sanitizeValue(
        rideData.customer.mobile || existingUser.mobile,
        '',
      ),
      email: sanitizeValue(existingUser.email, ''), // Sanitizar email
      city: canonicalPassengerCity(),
      pushToken: sanitizeValue(existingUser.pushToken, null),
      rideHistory: {
        ...existingUser.rideHistory, // Preservar el historial existente
        lastOrigin: {
          address: sanitizeValue(pickup?.addressName, ''),
          lat: pickup?.coords?.lat || null,
          lng: pickup?.coords?.lng || null,
        },
        lastDestination: {
          address: sanitizeValue(drop?.addressName, ''),
          lat: drop?.coords?.lat || null,
          lng: drop?.coords?.lng || null,
        },
      },
      updatedAt: new Date().getTime(),
      webFlag: true,
    };

    // Limpiar cualquier valor undefined del objeto
    const cleanUpdatedUser = Object.fromEntries(
      Object.entries(updatedUser).map(([key, value]) => [
        key,
        value === undefined ? null : value,
      ]),
    );

    try {
      // Usar editUser con el UID y los datos actualizados
      await dispatch(editUser(existingUser.uid, cleanUpdatedUser));

      // Actualizar el estado local
      setRideData({
        ...rideData,
        customer: { ...cleanUpdatedUser, uid: existingUser.uid },
      });

      await dispatch(fetchUsersOnce());
      return { ...cleanUpdatedUser, uid: existingUser.uid };
    } catch (err) {
      throw err;
    }
  };

  return {
    confirmBooking,
    estimateTripData,
    estimateRouteDetails,
    t,
    loadCustomers,
    loadDrivers,
    loadCurrentUser,
    updateSelectedCustomer,
    updateCustomerName,
    updatePickupAddress,
    updateDropAddress,
    updateCarDetails,
    updateInstructions,
    updateCustomerPhone,
    setEstimateModalStatus,
    createNewBasicUser,
    updateBasicUser,
    estimateModalStatus,
    loading,
    customers,
    state,
    rideData,
    auth,
    usersData,
    estimateData,
    settings,
    cartypes,
    name,
    city,
    setCity,
  };
};

export default useAddBooking;
